import React, { FormEventHandler, MouseEvent, MutableRefObject, SyntheticEvent } from 'react';
import classNames from 'classnames';
import { KeyboardShortcut, Key } from '@repo/components/tooltips';
import styles from './Note.module.scss';
import { PenIcon, XIcon } from '@repo/icons';
import { NoteDetails } from './NoteDetails';
import {
  ReadOnlyTextEditorContent,
  FollowupIcon,
  markdownToPlainText,
} from '@repo/components/text-editor';
import NoteModel, { removeNotePrompt } from '@repo/utils/models/Note';

interface NoteProps {
  note: NoteModel;
  selected: boolean;
  active?: boolean;
  removeable?: boolean;
  details?: boolean;
  inline?: boolean;
  keyboardShortcut?: boolean;
  actionsClassName?: string;
  inputRef?: MutableRefObject<HTMLInputElement>;
  onSubmit?: FormEventHandler;
  onSelect?: (note: NoteModel, e: MouseEvent) => void;
  onBlur?: (note: NoteModel, e: SyntheticEvent) => void;
  onDelete?: (note: NoteModel) => void;
  onNoteActivate?: (note: NoteModel) => void;
  readOnly?: boolean;
  // will only work for inline notes right now
  omitActions?: boolean;
}

function ShortcutPreview({ active = false, shortcut = null, children }) {
  if (!active) {
    return children;
  }

  return (
    <KeyboardShortcut placement="bottom" shortcut={shortcut}>
      {children}
    </KeyboardShortcut>
  );
}

export class NoteView extends React.PureComponent<NoteProps> {
  note: MutableRefObject<HTMLElement> = React.createRef();

  constructor(props) {
    super(props);
  }

  handleSelect(e = null) {
    if (this.props.readOnly) {
      return;
    }

    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // we want to allow the user to select note text for copy and paste without
    // selecting the note for editing purposes.
    const selectedText: Selection = window.getSelection();

    if (e && selectedText) {
      const someTextSelected =
        (selectedText?.focusOffset || 0) != (selectedText?.anchorOffset || 0);
      const isInTarget = e.target.contains(selectedText?.anchorNode?.parentNode);

      if (someTextSelected && isInTarget) {
        return;
      }
    }

    if (this.props.onSelect) {
      this.props.onSelect(this.props.note, e);
    }
  }

  handleBlur(e) {
    if (e) {
      e.preventDefault();
    }

    if (this.props.onBlur) {
      this.props.onBlur(this.props.note, e);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.active !== this.props.active) {
      const active = this.props.active;

      if (active && this.props.onNoteActivate) {
        this.props.onNoteActivate(this.props.note);
      }
    }
  }

  get renderActions() {
    return (
      <>
        <ShortcutPreview
          active={this.props.keyboardShortcut}
          shortcut={
            <>
              <Key>E</Key>Edit Note
            </>
          }
        >
          <button className="btn-none" type="button">
            <PenIcon width={20} height={20} hoverOpacity />
          </button>
        </ShortcutPreview>
        <ShortcutPreview
          active={this.props.keyboardShortcut}
          shortcut={
            <>
              <Key>#</Key>Delete Note
            </>
          }
        >
          <button
            onClick={removeNotePrompt(() => this.props.onDelete(this.props.note))}
            className="btn-none"
            type="button"
          >
            <XIcon width={15} height={15} hoverOpacity />
          </button>
        </ShortcutPreview>
      </>
    );
  }

  renderInline(note) {
    return (
      <span
        className={classNames(styles.inline, 'inline-note')}
        onClick={this.handleSelect.bind(this)}
      >
        <span className={styles.snippet}>{markdownToPlainText(note.content)}</span>
        {!this.props.omitActions && (
          <span className={classNames(styles.inlineActions, this.props.actionsClassName)}>
            {this.renderActions}
          </span>
        )}
      </span>
    );
  }

  render() {
    if (this.props.inline) {
      return this.renderInline(this.props.note);
    }

    return (
      <div
        onClick={this.handleSelect.bind(this)}
        className={classNames(styles.note, this.props.readOnly && styles.readOnly)}
      >
        <div className={styles.container}>
          <div className={styles.content}>
            <ReadOnlyTextEditorContent
              value={this.props.note.content}
              updated={this.props.note.updated}
              followupOccasion={this.props.note.reminder?.date}
              clickableMentions={!this.props.readOnly}
            />
          </div>
          <div className={styles.footer}>
            <div className={styles.footerContent}>
              <NoteDetails note={this.props.note} />
              <FollowupIcon followupOccasion={this.props.note.reminder?.date} precedeWithBullet />
            </div>
            <div className={classNames(styles.footerActions, this.props.actionsClassName)}>
              {!this.props.readOnly && this.renderActions}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
